import {
    AutoFixHigh,
    Chat as ChatIcon,
    Cable as ConnectIcon,
    Dashboard as DashboardIcon,
    Folder as FolderIcon,
    GridOff,
    GridOn,
    GroupWork,
    NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import {
    Box,
    Breadcrumbs,
    Chip,
    Divider,
    Link,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Typography,
    useTheme
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import ReactFlow, {
    Background,
    BackgroundVariant,
    ControlButton,
    Controls,
    Edge,
    Connection as FlowConnection,
    MiniMap,
    Node,
    OnConnect,
    ReactFlowInstance,
} from 'reactflow';
import 'reactflow/dist/style.css';
import './canvas.performance.css';

import { useCanvasKeyboardShortcuts } from '../hooks/useCanvasKeyboardShortcuts.js';
import { initializeDataSources } from '../services/dataSourceInit.js';
import { useCanvasStore, selectActiveCanvasId, selectConnectionsForCanvas, selectWidgetsForCanvas } from '../stores/canvasStore.js';
import { shallow } from 'zustand/shallow';
import type { Widget } from '../types/index.js';
import { validateConnection } from '../utils/connectionValidation.js';
import { createBulkConnections, getBulkConnectionSuggestions } from '../utils/smartConnect.js';
import { getTypeColor } from '../utils/typeColors.js';
import { widgetRegistry } from '../utils/widgetRegistry.js';
import { AgentChatMinimal } from './AgentChatMinimal.js';
import { ConnectionLine } from './ConnectionLine.js';
import { DiscoveryNode } from './DiscoveryNode.js';
import { MiniMapNode } from './MiniMapNode.js';
import { WidgetNode } from './WidgetNode.js';
// import { useCanvasMode } from '../hooks/useCanvasMode.js';

const nodeTypes = {
  widget: WidgetNode,
  discovery: DiscoveryNode,
};

export const Canvas: React.FC = () => {
  const theme = useTheme();
  // const canvasMode = useCanvasMode(); // TODO: Use for responsive behavior
  const activeCanvasId = useCanvasStore(selectActiveCanvasId);
  const activeCanvas = useCanvasStore(
    useCallback((state) => state.canvases.find(c => c.id === activeCanvasId), [activeCanvasId])
  );
  // Lock down Market and Profile canvases from user edits; custom canvases can be editable later
  const isReadOnly = activeCanvas?.kind === 'market' || activeCanvas?.kind === 'profile';
  const updateViewport = useCanvasStore((state) => state.updateViewport);
  const updateGridSettings = useCanvasStore((state) => state.updateGridSettings);
  const moveWidget = useCanvasStore((state) => state.moveWidget);
  const moveDiscoveryNode = useCanvasStore((state) => state.moveDiscoveryNode);
  const createConnection = useCanvasStore((state) => state.createConnection);
  const removeConnection = useCanvasStore((state) => state.removeConnection);
  const createGroup = useCanvasStore((state) => state.createGroup);
  const zoomOut = useCanvasStore((state) => state.zoomOut);
  
  // Stable style/options to reduce re-renders
  const defaultEdgeOptions = React.useMemo(
    () => ({
      type: 'smoothstep' as const,
      style: { strokeWidth: 1.5, opacity: 0.9 },
    }),
    []
  );

  const miniMapStyle = React.useMemo(() => ({
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: 'none',
    // Offset from the left so it doesn't overlap the left-side toolbar
    left: 48,
  }), []);

  const backgroundStyle = React.useMemo(() => ({
    backgroundColor: 'transparent',
  }), []);

  // Stable pan-drag buttons array to avoid prop identity churn
  const panOnDragButtons = React.useMemo(() => [1, 2] as const, []);

  // Stable grid values
  const gridSize = useMemo(() => activeCanvas?.gridSettings.size ?? 20, [activeCanvas?.gridSettings.size]);
  const gridEnabled = useMemo(() => activeCanvas?.gridSettings.enabled ?? true, [activeCanvas?.gridSettings.enabled]);
  const snapGrid = useMemo(() => [gridSize, gridSize] as [number, number], [gridSize]);

  // Initialize data sources on mount
  React.useEffect(() => {
    console.log('[Canvas] Initializing data sources...');
    const sources = initializeDataSources();
    console.log('[Canvas] Initialized sources:', sources);
  }, []);
  
  // Smart connect menu state
  const [smartConnectAnchor, setSmartConnectAnchor] = React.useState<null | HTMLElement>(null);
  const [connectionSuggestions, setConnectionSuggestions] = React.useState<ReturnType<typeof getBulkConnectionSuggestions>>([]);
  
  // Agent chat state
  const [showAgentChat, setShowAgentChat] = useState(false);
  // Default agent overlay to visible on Market canvas only
  React.useEffect(() => {
    if (activeCanvas?.kind === 'market') {
      setShowAgentChat(true);
    } else {
      setShowAgentChat(false);
    }
    // run when canvas kind or id changes
  }, [activeCanvas?.id, activeCanvas?.kind]);
  const [isPanning, setIsPanning] = useState(false);
  const flowClassName = React.useMemo(
    () => ['akem-flow', isReadOnly ? 'readonly' : '', isPanning ? 'panning' : ''].filter(Boolean).join(' '),
    [isPanning, isReadOnly]
  );

  const allowedPanButtons = React.useMemo(() => (isReadOnly ? [2] : [...panOnDragButtons]), [isReadOnly, panOnDragButtons]);

  const isUserPanEvent = React.useCallback((event?: any) => {
    if (!event) return false;
    // ReactFlow provides React SyntheticEvents; prefer the nativeEvent
    const ne: any = event?.nativeEvent ?? event;
    // Mouse: require a pressed button and check allowed button
    if (ne && typeof ne.buttons === 'number' && typeof ne.button === 'number') {
      return ne.buttons > 0 && allowedPanButtons.includes(ne.button as number);
    }
    // Touch: any active touch counts as a pan gesture
    if (ne && Array.isArray(ne.touches) || typeof ne.touches === 'object') {
      try {
        return (ne.touches?.length ?? 0) > 0;
      } catch {
        return false;
      }
    }
    return false;
  }, [allowedPanButtons]);

  // Capture initial viewport only once to avoid ReactFlow re-initialization loops
  const initialViewportRef = React.useRef(activeCanvas?.viewport);
  const rfInitDoneRef = React.useRef(false);
  const activeCanvasIdRef = React.useRef<string | undefined>(activeCanvasId);
  React.useEffect(() => { activeCanvasIdRef.current = activeCanvasId; }, [activeCanvasId]);
  const handleInit = useCallback((instance: ReactFlowInstance) => {
    if (rfInitDoneRef.current) return;
    rfInitDoneRef.current = true;
    try {
      if (initialViewportRef.current) {
        instance.setViewport(initialViewportRef.current);
      }
    } catch {}
  }, []);

  // Get visible widgets and connections directly from the store with shallow equality
  const visibleWidgets = useCanvasStore(
    useMemo(() => (state) => activeCanvasId ? selectWidgetsForCanvas(activeCanvasId)(state) : [], [activeCanvasId])
  );
  const visibleConnections = useCanvasStore(
    useMemo(() => (state) => activeCanvasId ? selectConnectionsForCanvas(activeCanvasId)(state) : [], [activeCanvasId])
  );

  // Get discovery nodes for current canvas
  const discoveryNodes = useMemo(() => {
    if (!activeCanvas) return [];
    return activeCanvas.discoveryNodes || [];
  }, [activeCanvas?.discoveryNodes, activeCanvasId]);

  // Convert widgets to React Flow nodes
  const nodes = useMemo<Node[]>(() => {
    // Store widget data for minimap access
    (window as any).__minimapNodeData = visibleWidgets.reduce((acc, widget) => {
      acc[widget.id] = widget;
      return acc;
    }, {} as Record<string, Widget>);
    
    const widgetNodes = visibleWidgets.map((widget) => ({
      id: widget.id,
      type: 'widget',
      position: widget.position,
      data: widget,
    })).sort((a, b) => a.id.localeCompare(b.id));
    
    // Add discovery nodes
    const discoveryFlowNodes = discoveryNodes.map((node) => ({
      id: node.id,
      type: 'discovery',
      position: node.position,
      data: node,
    })).sort((a, b) => a.id.localeCompare(b.id));
    
    return [...widgetNodes, ...discoveryFlowNodes];
  }, [visibleWidgets, discoveryNodes]);

  // Convert connections to React Flow edges
  const edges = useMemo<Edge[]>(() => {
    const mapped = visibleConnections.map((connection) => {
      // Find the source widget and output definition to get type color
      const sourceWidget = visibleWidgets.find(w => w.id === connection.source.widgetId);
      const sourceWidgetDef = sourceWidget ? widgetRegistry.get(sourceWidget.type) : undefined;
      const outputDef = sourceWidgetDef?.outputDefinitions?.find(def => def.id === connection.source.outputId);
      const edgeColor = getTypeColor(outputDef?.schema);
      
      // For widget groups, check the widget's outputs directly
      if (sourceWidget?.type === 'widgetGroup') {
        const output = sourceWidget.outputs.find(o => o.id === connection.source.outputId);
        if (output?.schema) {
          const color = getTypeColor(output.schema);
          if (color) {
            return {
              id: connection.id,
              source: connection.source.widgetId,
              target: connection.target.widgetId,
              sourceHandle: connection.source.outputId,
              targetHandle: connection.target.inputId,
              type: 'smoothstep',
              style: {
                stroke: color,
                strokeWidth: 2,
                opacity: 0.95,
              },
            };
          }
        }
      }

      return {
        id: connection.id,
        source: connection.source.widgetId,
        target: connection.target.widgetId,
        sourceHandle: connection.source.outputId,
        targetHandle: connection.target.inputId,
        type: 'smoothstep',
        style: {
          stroke: edgeColor || 'rgba(255,255,255,0.35)',
          strokeWidth: edgeColor ? 2 : 1.5,
          opacity: edgeColor ? 0.95 : 0.75,
        },
      };
    });
    // Keep a stable order to avoid churn
    return mapped.sort((a, b) => a.id.localeCompare(b.id));
  }, [visibleConnections, visibleWidgets]);

  // Use fully controlled nodes/edges from store-derived memos to avoid sync loops

  // No local sync effects — ReactFlow consumes props directly

  // Handle node position changes
  // We don't mirror internal node changes; we persist on drag stop only
  
  // Handle selection changes
  const handleSelectionChange = useCallback(({ nodes }: { nodes: Node[] }) => {
    const nextSelectedIds = nodes.map(node => node.id).sort();
    setSelectedNodes((prev) => {
      const prevSorted = [...prev].sort();
      if (prevSorted.length === nextSelectedIds.length && prevSorted.every((id, i) => id === nextSelectedIds[i])) {
        return prev;
      }
      (window as any).__selectedNodes = nextSelectedIds;
      return nextSelectedIds;
    });
  }, [isReadOnly]);

  // Handle edge changes
  const handleEdgesDelete = useCallback(
    (deleted: Edge[]) => {
      if (isReadOnly) return;
      const cid = activeCanvasIdRef.current;
      if (!cid) return;
      deleted.forEach((e) => removeConnection(cid, e.id));
    },
    [isReadOnly, removeConnection]
  );

  // Handle new connections with validation
  const handleConnect: OnConnect = useCallback(
    (params: FlowConnection) => {
      if (isReadOnly) return;
      const cid = activeCanvasIdRef.current;
      if (!cid || !params.source || !params.target) return;

      const state = useCanvasStore.getState();
      const canvas = state.canvases.find(c => c.id === cid);
      if (!canvas) return;

      const sourceWidget = canvas.widgets.find(w => w.id === params.source);
      const targetWidget = canvas.widgets.find(w => w.id === params.target);
      if (!sourceWidget || !targetWidget) return;

      const validation = validateConnection(
        sourceWidget,
        targetWidget,
        params.sourceHandle || 'default',
        params.targetHandle || 'default'
      );
      if (!validation.valid) {
        console.warn('Invalid connection:', validation.reason);
        return;
      }

      createConnection(cid, {
        source: {
          widgetId: params.source,
          outputId: params.sourceHandle || 'default',
        },
        target: {
          widgetId: params.target,
          inputId: params.targetHandle || 'default',
        },
      });
    },
    [createConnection, isReadOnly]
  );

  // Handle node drag stop (memoized)
  const handleNodeDragStop = useCallback((_e: React.MouseEvent, node: Node) => {
    if (isReadOnly) return;
    const cid = activeCanvasIdRef.current;
    const canvas = cid ? useCanvasStore.getState().canvases.find(c => c.id === cid) : undefined;
    if (!cid || !canvas) return;
    const gridSize = canvas.gridSettings.size;
    const shouldSnap = canvas.gridSettings.enabled;
    const snap = (value: number) => Math.round(value / gridSize) * gridSize;
    const snappedPosition = shouldSnap
      ? { x: snap(node.position.x), y: snap(node.position.y) }
      : node.position;

    const isWidget = visibleWidgets.some((w) => w.id === node.id);
    if (isWidget) {
      moveWidget(cid, node.id, snappedPosition);
    } else {
      const isDiscovery = discoveryNodes.some((n) => n.id === node.id);
      if (isDiscovery) {
        moveDiscoveryNode(cid, node.id, snappedPosition);
      }
    }
  }, [discoveryNodes, isReadOnly, moveDiscoveryNode, moveWidget, visibleWidgets]);

  // Toggle grid
  const toggleGrid = useCallback(() => {
    const cid = activeCanvasIdRef.current;
    if (!cid || !activeCanvas) return;
    updateGridSettings(cid, { enabled: !activeCanvas.gridSettings.enabled });
  }, [activeCanvas, updateGridSettings]);

  // Track selected nodes
  const [selectedNodes, setSelectedNodes] = React.useState<string[]>([]);

  // Handle smart connect button click
  const handleSmartConnect = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (!activeCanvas || isReadOnly) return;

    // Get selected widget IDs
    const selectedWidgets = selectedNodes;
    
    if (selectedWidgets.length >= 2) {
      const suggestions = getBulkConnectionSuggestions(activeCanvas.widgets, selectedWidgets);
      setConnectionSuggestions(suggestions);
      setSmartConnectAnchor(event.currentTarget);
    }
  }, [activeCanvas, isReadOnly, selectedNodes]);

  // Handle connection suggestion selection
  const handleApplySuggestion = useCallback((suggestionIndex: number) => {
    if (!activeCanvas || isReadOnly) return;

    const suggestion = connectionSuggestions[suggestionIndex];
    if (suggestion) {
      createBulkConnections(suggestion, (conn) => {
        createConnection(activeCanvas.id, conn);
      });
    }

    setSmartConnectAnchor(null);
  }, [activeCanvas, connectionSuggestions, createConnection, isReadOnly]);

  // Handle group creation
  const handleCreateGroup = useCallback(() => {
    if (!activeCanvas || isReadOnly || selectedNodes.length < 2) return;

    const groupName = prompt('Enter a name for the widget group:');
    if (groupName) {
      createGroup(activeCanvas.id, selectedNodes, groupName);
      setSelectedNodes([]);
    }
  }, [activeCanvas, createGroup, isReadOnly, selectedNodes]);

  // Setup keyboard shortcuts
  useCanvasKeyboardShortcuts();

  if (!activeCanvas) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'text.secondary',
        }}
      >
        No active canvas
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Breadcrumbs */}
      {activeCanvas.breadcrumbs && activeCanvas.breadcrumbs.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />}
            sx={{ flex: 1 }}
          >
            {activeCanvas.breadcrumbs.map((crumb, index) => {
              const isLast = index === activeCanvas.breadcrumbs!.length - 1;
              const Icon = crumb.type === 'canvas' ? DashboardIcon : FolderIcon;
              
              if (isLast) {
                return (
                  <Box key={crumb.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Icon fontSize="small" color="primary" />
                    <Typography color="text.primary" variant="body2">
                      {crumb.name}
                    </Typography>
                  </Box>
                );
              }
              
              return (
                <Link
                  key={crumb.id}
                  underline="hover"
                  color="inherit"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (crumb.type === 'canvas') {
                      zoomOut(activeCanvas.id);
                    }
                  }}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                >
                  <Icon fontSize="small" />
                  <Typography variant="body2">{crumb.name}</Typography>
                </Link>
              );
            })}
          </Breadcrumbs>
          <Chip
            label={`${visibleWidgets.length} widgets`}
            size="small"
            variant="outlined"
          />
        </Box>
      )}
      {/* Canvas root: used to align floating input 16px below this top */}
      <Box id="akem-canvas-root" sx={{ position: 'relative', height: '100%' }}>
      <ReactFlow
        className={flowClassName}
        onInit={handleInit}
        nodes={nodes}
        edges={edges}
        onEdgesDelete={handleEdgesDelete}
        onConnect={handleConnect}
        onSelectionChange={handleSelectionChange}
        onNodeDragStop={handleNodeDragStop}
        onMoveStart={(event) => {
          if (isUserPanEvent(event)) {
            // eslint-disable-next-line no-console
            console.log(`[Canvas] Panning start at ${new Date().toISOString()}`);
            setIsPanning(true);
          }
        }}
        onMoveEnd={() => {
          // eslint-disable-next-line no-console
          console.log(`[Canvas] Panning end at ${new Date().toISOString()}`);
          setIsPanning(false);
        }}
        nodeTypes={nodeTypes}
        attributionPosition="bottom-right"
        defaultViewport={initialViewportRef.current}
        minZoom={0.1}
        maxZoom={2}
        panOnScroll={true}
        zoomOnScroll={false}
        zoomOnDoubleClick={false}
        // Keep left-drag free for widget interactions on read-only canvases
        panOnDrag={isReadOnly ? [2] : [...panOnDragButtons]}
        noPanClassName="nopan"
        noDragClassName="nodrag"
        selectionOnDrag={!isReadOnly}
        snapToGrid={false}
        snapGrid={snapGrid}
        connectionLineComponent={ConnectionLine}
        defaultEdgeOptions={defaultEdgeOptions}
        nodesDraggable={!isReadOnly}
        nodesConnectable={!isReadOnly}
        elementsSelectable
      >
        <Background
          variant={gridEnabled ? BackgroundVariant.Dots : BackgroundVariant.Cross}
          gap={gridSize}
          size={1}
          color="#2E2E2E"
          style={backgroundStyle}
        />
        <Controls showInteractive={false}>
          <ControlButton onClick={toggleGrid} title="Toggle Grid">
            {activeCanvas.gridSettings.enabled ? <GridOn /> : <GridOff />}
          </ControlButton>
          <ControlButton 
            onClick={handleSmartConnect} 
            title="Smart Connect"
            disabled={isReadOnly || selectedNodes.length < 2}
          >
            <AutoFixHigh />
          </ControlButton>
          <ControlButton 
            onClick={handleCreateGroup} 
            title="Group Selected"
            disabled={isReadOnly || selectedNodes.length < 2}
          >
            <GroupWork />
          </ControlButton>
          {!isReadOnly && (
            <ControlButton
              onClick={() => setShowAgentChat(!showAgentChat)}
              title="Toggle Agent Chat"
            >
              <ChatIcon />
            </ControlButton>
          )}
        </Controls>
        <MiniMap
          nodeComponent={MiniMapNode}
          nodeStrokeColor={theme.palette.divider}
          nodeStrokeWidth={1}
          nodeColor={theme.palette.background.paper}
          nodeBorderRadius={4}
          maskColor={`${theme.palette.background.default}CC`}
          pannable
          zoomable
          position="bottom-left"
          style={miniMapStyle}
        />
      </ReactFlow>
      </Box>
      
      {/* Smart Connect Menu */}
      <Menu
        anchorEl={smartConnectAnchor}
        open={Boolean(smartConnectAnchor)}
        onClose={() => setSmartConnectAnchor(null)}
        PaperProps={{
          sx: { minWidth: 300 }
        }}
      >
        <MenuItem disabled>
          <Typography variant="subtitle2">Smart Connection Suggestions</Typography>
        </MenuItem>
        <Divider />
        {connectionSuggestions.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No compatible connections found
            </Typography>
          </MenuItem>
        ) : (
          connectionSuggestions.map((suggestion, index) => (
            <MenuItem key={index} onClick={() => handleApplySuggestion(index)}>
              <ListItemIcon>
                <ConnectIcon />
              </ListItemIcon>
              <ListItemText
                primary={`Connect ${suggestion.connections.length} ${suggestion.connections.length === 1 ? 'output' : 'outputs'}`}
                secondary={
                  <Box>
                    <Typography variant="caption" component="div">
                      From: {suggestion.sourceWidget.config.title || suggestion.sourceWidget.type}
                    </Typography>
                    <Typography variant="caption" component="div">
                      To: {suggestion.targetWidget.config.title || suggestion.targetWidget.type}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {suggestion.connections.map(c => `${c.outputName} → ${c.inputName}`).join(', ')}
                    </Typography>
                  </Box>
                }
              />
            </MenuItem>
          ))
        )}
      </Menu>
      
      {/* Agent Chat Overlay */}
      {showAgentChat && (
        <AgentChatMinimal onClose={() => setShowAgentChat(false)} />
      )}

      {/* Agent status feed removed: Run widget now owns run context and status */}
    </Box>
  );
};
